import React from 'react';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

import './PageFooter.css';

function PageFooter({
  reportData,
  currentStatus,
  isDownloadLoading,
  isTriggerLoading,
  isPreviewLoading,
  isLaunchLoading,
  triggerError,
  triggerConflict,
  onDownload,
  onRetrigger,
  onPreview,
  onLaunch,
  onClose,
  showOnlyClose,
}) {
  const l = useInvertedLocalize(invertedMessages);

  if (showOnlyClose) {
    return (
      <Box className='PageFooter'>
        <Button type='button' kind='outlined' size='medium' onClick={onClose}>
          {l('Close')}
        </Button>
      </Box>
    );
  }

  const anyLoading =
    isDownloadLoading ||
    isTriggerLoading ||
    isPreviewLoading ||
    isLaunchLoading;
  const retriggerDisabled = anyLoading || !!triggerConflict;
  const showActionButtons = currentStatus !== 'RUNNING' && reportData;

  return (
    <Box className='PageFooter'>
      {triggerError && (
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          className='PageFooter-inlineError'>
          {triggerError}
        </Typography>
      )}
      {triggerConflict && (
        <Typography
          kind='body3'
          desktop='body3-d'
          mobile='body3-d'
          className='PageFooter-inlineError'>
          {triggerConflict.message ||
            l('A pre-launch analysis is already running for this campaign.')}
        </Typography>
      )}

      {showActionButtons && (
        <>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={anyLoading}
            leftIcon={<Icon kind='Download' size='xSmall' />}
            onClick={onDownload}>
            {isDownloadLoading ? l('Downloading…') : l('Download Report')}
          </Button>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={retriggerDisabled}
            leftIcon={<Icon kind='Retry' size='xSmall' />}
            onClick={onRetrigger}>
            {isTriggerLoading ? l('Retriggering…') : l('Re-trigger Analysis')}
          </Button>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={anyLoading}
            leftIcon={<Icon kind='Preview' size='xSmall' />}
            onClick={onPreview}>
            {l('Preview')}
          </Button>
          <Button
            type='button'
            kind='outlined'
            size='medium'
            isDisabled={anyLoading}
            leftIcon={<Icon kind='LaunchRocket' size='xSmall' />}
            onClick={onLaunch}>
            {l('Launch Campaign')}
          </Button>
        </>
      )}
      <Button type='button' kind='outlined' size='medium' onClick={onClose}>
        {l('Close')}
      </Button>
    </Box>
  );
}

PageFooter.propTypes = {
  reportData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  currentStatus: PropTypes.string,
  isDownloadLoading: PropTypes.bool.isRequired,
  isTriggerLoading: PropTypes.bool.isRequired,
  isPreviewLoading: PropTypes.bool,
  isLaunchLoading: PropTypes.bool,
  triggerError: PropTypes.string,
  triggerConflict: PropTypes.shape({ message: PropTypes.string }),
  onDownload: PropTypes.func.isRequired,
  onRetrigger: PropTypes.func.isRequired,
  onPreview: PropTypes.func.isRequired,
  onLaunch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  showOnlyClose: PropTypes.bool,
};

PageFooter.defaultProps = {
  reportData: null,
  currentStatus: '',
  isPreviewLoading: false,
  isLaunchLoading: false,
  triggerError: null,
  triggerConflict: null,
  showOnlyClose: false,
};

export default PageFooter;
